import React from 'react';
import { UserCog, DollarSign, ClipboardCheck, Shield, Zap, AlertTriangle, Loader2, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import { Progress } from '../../ui/progress';
import UserAvatar from '../../UserAvatar';
import { STATUS_COLORS } from './StatusTracker';

export const CleanerAssignmentCard = ({ appt, onOpenAssign, teamCount = 0, teamNames = [] }) => (
  <Card className="border-0 shadow-md" data-testid="cleaner-assignment">
    <CardHeader className="py-3 border-b border-zinc-100">
      <CardTitle className="text-sm font-bold flex items-center gap-2"><UserCog className="h-4 w-4 text-[#F56F05]" />Assigned Team</CardTitle>
    </CardHeader>
    <CardContent className="p-4">
      {appt.cleaner_name ? (
        <div className="mb-3">
          <div className="flex items-center gap-3">
            <UserAvatar
              user={{ full_name: appt.cleaner_name, photo_url: appt.cleaner_photo_url, role: 'cleaner' }}
              size="md"
              testId="cleaner-assignment-avatar"
            />
            <div>
              <p className="font-semibold text-zinc-900">{appt.cleaner_name}</p>
              <p className="text-xs text-zinc-500">Lead Guru</p>
            </div>
          </div>
          {teamCount > 1 && (
            <p className="text-xs text-zinc-500 mt-2" data-testid="team-supporting-gurus">
              + {teamCount - 1} more: {teamNames.slice(1).join(', ')}
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-zinc-400 mb-3">No gurus assigned</p>
      )}
      <Button size="sm" variant="outline" className="w-full" onClick={onOpenAssign} data-testid="reassign-btn">
        <UserCog className="h-4 w-4 mr-1" />
        {appt.cleaner_name ? 'Edit team' : 'Assign gurus'}
      </Button>
    </CardContent>
  </Card>
);

export const BillingPanel = ({
  serviceCost, addOnsCost, tax, total, invoice,
  canCharge, hasClockOut, hasAfterPhoto, qcPassed,
  appt, charging, onConfirmCharge,
}) => (
  <Card className="border-0 shadow-lg border-l-4 border-l-[#F56F05]" data-testid="billing-panel">
    <CardHeader className="py-3 border-b border-zinc-100">
      <CardTitle className="text-sm font-bold flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#F56F05]" />Billing</CardTitle>
    </CardHeader>
    <CardContent className="p-4 space-y-3">
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-zinc-500">Service</span><span className="font-medium">${serviceCost.toFixed(2)}</span></div>
        {addOnsCost > 0 && <div className="flex justify-between"><span className="text-zinc-500">Add-Ons</span><span className="font-medium">${addOnsCost.toFixed(2)}</span></div>}
        {tax > 0 && <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span className="font-medium">${tax.toFixed(2)}</span></div>}
        <Separator />
        <div className="flex justify-between text-base"><span className="font-bold">Total</span><span className="font-bold text-[#F56F05]">${total.toFixed(2)}</span></div>
      </div>

      {invoice && (
        <div className={`p-3 rounded-lg text-center ${
          invoice.status === 'Paid' ? 'bg-green-50' : invoice.status === 'Payment Failed' ? 'bg-red-50' : 'bg-zinc-50'
        }`}>
          <p className="text-xs font-bold text-zinc-500 mb-1">Invoice Status</p>
          <Badge className={invoice.status === 'Paid' ? 'bg-green-500' : invoice.status === 'Payment Failed' ? 'bg-red-500' : 'bg-zinc-500'}>
            {invoice.status}
          </Badge>
        </div>
      )}

      {canCharge && (
        <Button className="w-full bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold shadow-lg"
          onClick={onConfirmCharge} disabled={charging} data-testid="confirm-charge-btn">
          {charging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
          Admin Confirm &amp; Charge
        </Button>
      )}

      {!canCharge && appt.status === 'Service Finished' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs font-bold text-yellow-700 mb-2">Cannot charge yet:</p>
          <ul className="text-xs text-yellow-600 space-y-1">
            {!hasClockOut && <li className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Missing clock-out</li>}
            {!hasAfterPhoto && <li className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Missing after photos</li>}
            {!qcPassed && <li className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />QC not passed</li>}
          </ul>
        </div>
      )}
    </CardContent>
  </Card>
);

export const QCPanel = ({ qcItems, qcPassed, qcProgress, checkedCount, requiredMissing, canQCPass, qcPassing, onQCPass }) => {
  if (qcItems.length === 0) {
    return (
      <Card className="border-0 shadow-md border-l-4 border-l-yellow-400">
        <CardContent className="p-3">
          <p className="text-xs text-yellow-700 font-medium flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> No QC template configured for this service.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-0 shadow-md" data-testid="admin-qc-panel">
      <CardHeader className="py-3 border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-[#F56F05]" />QC Checklist
          </CardTitle>
          {qcPassed ? (
            <Badge className="bg-green-500 text-white text-[10px]">Passed</Badge>
          ) : (
            <span className="text-xs font-bold text-zinc-500">{checkedCount}/{qcItems.length}</span>
          )}
        </div>
        <Progress value={qcProgress} className="mt-2 h-1.5" />
      </CardHeader>
      <CardContent className="p-0 max-h-[250px] overflow-y-auto">
        {qcItems.map(item => (
          <div key={item.id} className={`flex items-center gap-2 px-3 py-2 text-xs border-b border-zinc-50 last:border-0 ${
            item.is_required && !item.is_checked ? 'bg-red-50/50' : ''
          }`} data-testid={`admin-qc-${item.id}`}>
            <div className={`flex-shrink-0 w-4 h-4 rounded flex items-center justify-center ${
              item.is_checked ? 'bg-green-500' : 'border border-zinc-300'
            }`}>
              {item.is_checked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
            </div>
            <span className={`flex-1 ${item.is_checked ? 'text-zinc-400 line-through' : 'text-zinc-700'}`}>
              {item.label_snapshot}
            </span>
            {item.is_required && !item.is_checked && (
              <X className="h-3 w-3 text-red-400 flex-shrink-0" />
            )}
          </div>
        ))}
      </CardContent>
      {!qcPassed && (
        <div className="p-3 border-t border-zinc-100">
          <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
            onClick={onQCPass} disabled={!canQCPass || qcPassing} data-testid="qc-pass-btn">
            {qcPassing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Shield className="h-4 w-4 mr-1" />}
            {canQCPass ? 'QC Pass' : `${requiredMissing.length} required items missing`}
          </Button>
        </div>
      )}
    </Card>
  );
};

export const QuickInfoCard = ({ appt }) => (
  <Card className="border-0 shadow-md">
    <CardContent className="p-4 space-y-3 text-sm">
      <div className="flex justify-between">
        <span className="text-zinc-500">Created</span>
        <span className="font-medium">{new Date(appt.created_at).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-zinc-500">Scheduled</span>
        <span className="font-medium">{new Date(appt.start_time).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'medium', timeStyle: 'short' })} PT</span>
      </div>
      <div className="flex justify-between">
        <span className="text-zinc-500">Status</span>
        <Badge className={STATUS_COLORS[appt.status]}>{appt.status}</Badge>
      </div>
    </CardContent>
  </Card>
);
